if (!customElements.get("collection-load-more")) {
  const selectors = {
    grid: "[data-products-grid]",
    section: "[data-section-id]",
    button: "[data-cload-button]",
    count: "[data-cload-count]",
    track: "[data-cload-track]",
    bar: "[data-cload-bar]",
    error: "[data-cload-error]",
    nextCategory: "[data-cload-next-category]",
    batchItem: ":scope > [data-product-grid-item], :scope > .cmedia-block",
    mediaBlock: ".cmedia-block",
    self: "collection-load-more",
  };

  const classes = {
    item: "cload-item",
    itemIn: "cload-item--in",
    complete: "cload--complete",
    deferred: "cload-deferred",
  };

  const aosAttributes = [
    "data-aos",
    "data-aos-delay",
    "data-aos-duration",
    "data-aos-anchor",
    "data-aos-easing",
  ];

  const timings = {
    staggerRow: 90,
    staggerCell: 45,
    staggerMax: 1200,
    slide: 400,
  };

  const prefetchMargin = "400px";

  customElements.define(
    "collection-load-more",
    class CollectionLoadMore extends HTMLElement {
      connectedCallback() {
        this.grid = this.closest(selectors.grid);
        if (!this.grid) return;

        this.button = this.querySelector(selectors.button);
        this.count = this.querySelector(selectors.count);
        this.track = this.querySelector(selectors.track);
        this.bar = this.querySelector(selectors.bar);
        this.errorMessage = this.querySelector(selectors.error);

        this.nextPage = parseInt(this.dataset.nextPage, 10) || 0;
        this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
        this.shown = parseInt(this.dataset.shown, 10) || 0;
        this.total = parseInt(this.dataset.total, 10) || 0;
        this.rowOffset = parseInt(this.dataset.batchRows, 10) || 0;
        this.offsettable = this.dataset.offsettable === "true";
        this.template = this.dataset.text || "";

        this.cache = new Map();
        this.loading = false;
        this.reduceMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

        this.onClick = this.onClick.bind(this);
        this.onIntent = this.onIntent.bind(this);
        this.onSlideEnd = this.onSlideEnd.bind(this);

        if (this.button) {
          this.button.addEventListener("click", this.onClick);
          this.button.addEventListener("pointerenter", this.onIntent);
          this.button.addEventListener("focus", this.onIntent);
          this.watchViewport();
        }

        this.syncNextCategory();
      }

      disconnectedCallback() {
        if (this.button) {
          this.button.removeEventListener("click", this.onClick);
          this.button.removeEventListener("pointerenter", this.onIntent);
          this.button.removeEventListener("focus", this.onIntent);
        }

        this.removeEventListener("transitionend", this.onSlideEnd);

        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        if (this.cache) this.cache.clear();
      }

      hasMore() {
        return this.nextPage > 0 && this.nextPage <= this.totalPages;
      }

      watchViewport() {
        if (!("IntersectionObserver" in window)) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.prefetch();
          },
          { rootMargin: prefetchMargin }
        );

        this.observer.observe(this);
      }

      pageUrl(page, forSection) {
        const url = new URL(window.location.href);
        url.searchParams.set("page", page);

        if (forSection) {
          url.searchParams.set("section_id", this.dataset.sectionId);
        } else {
          url.searchParams.delete("section_id");
        }

        return url.toString();
      }

      prefetch() {
        const page = this.nextPage;
        if (!this.hasMore() || this.cache.has(page)) return;

        const request = fetch(this.pageUrl(page, true)).then((response) => {
          if (!response.ok) throw new Error(`Load more: ${response.status}`);
          return response.text();
        });

        request.catch(() => this.cache.delete(page));

        this.cache.set(page, request);
      }

      onIntent() {
        this.prefetch();
      }

      onClick(event) {
        event.preventDefault();
        this.load();
      }

      async load() {
        if (this.loading || !this.hasMore()) return;

        const page = this.nextPage;
        this.loading = true;
        this.setBusy(true);
        this.toggleError(false);

        try {
          this.prefetch();
          this.append(await this.cache.get(page));
        } catch (error) {
          this.toggleError(true);
        } finally {
          this.loading = false;
          this.setBusy(false);
        }
      }

      append(html) {
        const incoming = new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector(selectors.grid);

        if (!incoming) throw new Error("Load more: no product grid in response");

        const source = incoming.querySelector(selectors.self);
        const batchRows = source
          ? parseInt(source.dataset.batchRows, 10) || 0
          : 0;
        const stillOffsettable = source
          ? source.dataset.offsettable === "true"
          : false;

        const columns = this.columnCount();
        const fragment = document.createDocumentFragment();
        const revealed = [];
        let products = 0;

        incoming.querySelectorAll(selectors.batchItem).forEach((node) => {
          if (node.matches(selectors.mediaBlock)) {
            if (!this.offsettable) return;
            this.shiftMediaBlock(node);
          } else {
            products += 1;
          }

          this.stripAos(node);
          node.classList.add(classes.item);
          node.style.setProperty(
            "--cload-delay",
            `${this.delayFor(revealed.length, columns)}ms`
          );

          revealed.push(node);
          fragment.appendChild(node);
        });

        const before = this.getBoundingClientRect().top;
        this.grid.insertBefore(fragment, this);
        const after = this.getBoundingClientRect().top;

        this.slideFrom(before - after);

        requestAnimationFrame(() => {
          revealed.forEach((node) => node.classList.add(classes.itemIn));
        });

        this.shown += products;
        this.nextPage += 1;
        this.rowOffset += batchRows;
        this.offsettable = this.offsettable && stillOffsettable;
        this.updateProgress();

        if (this.hasMore()) {
          this.button.href = this.pageUrl(this.nextPage, false);
          this.prefetch();
        } else {
          this.complete();
        }
      }

      columnCount() {
        const template = window.getComputedStyle(this.grid).gridTemplateColumns;
        const count = template ? template.split(" ").filter(Boolean).length : 1;
        return Math.max(count, 1);
      }

      delayFor(index, columns) {
        if (this.reduceMotion) return 0;

        const row = Math.floor(index / columns);
        const cell = index % columns;

        return Math.min(
          row * timings.staggerRow + cell * timings.staggerCell,
          timings.staggerMax
        );
      }

      shiftMediaBlock(node) {
        const row = parseInt(node.style.getPropertyValue("--cmedia-row"), 10);
        if (!row) return;

        node.style.setProperty("--cmedia-row", row + this.rowOffset);
      }

      stripAos(node) {
        const targets = node.matches("[data-aos]") ? [node] : [];
        targets.push(...node.querySelectorAll("[data-aos]"));

        targets.forEach((element) => {
          aosAttributes.forEach((attribute) =>
            element.removeAttribute(attribute)
          );
        });
      }

      slideFrom(delta) {
        if (this.reduceMotion || Math.abs(delta) < 2) return;

        this.style.transition = "none";
        this.style.transform = `translateY(${delta}px)`;
        void this.offsetHeight;

        requestAnimationFrame(() => {
          this.style.transition = `transform ${timings.slide}ms cubic-bezier(0.2, 0.7, 0.3, 1)`;
          this.style.transform = "";
          this.addEventListener("transitionend", this.onSlideEnd, {
            once: true,
          });
        });
      }

      onSlideEnd() {
        this.style.transition = "";
        this.style.transform = "";
      }

      updateProgress() {
        if (this.count && this.template) {
          this.count.textContent = this.template
            .replace("||shown||", this.shown)
            .replace("||total||", this.total);
        }

        const percent = this.total
          ? Math.round((this.shown / this.total) * 100)
          : 100;

        if (this.bar) {
          this.bar.style.setProperty("--cload-progress", `${percent}%`);
        }

        if (this.track) this.track.setAttribute("aria-valuenow", this.shown);
      }

      complete() {
        this.classList.add(classes.complete);

        if (this.button) {
          this.button.remove();
          this.button = null;
        }

        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        this.syncNextCategory();
      }

      syncNextCategory() {
        const section = this.parentElement
          ? this.parentElement.closest(selectors.section)
          : null;
        const nextCategory = section
          ? section.querySelector(selectors.nextCategory)
          : null;

        if (nextCategory) {
          nextCategory.classList.toggle(classes.deferred, this.hasMore());
        }
      }

      setBusy(state) {
        if (!this.button) return;

        if (state) {
          this.button.setAttribute("aria-busy", "true");
        } else {
          this.button.removeAttribute("aria-busy");
        }
      }

      toggleError(state) {
        if (!this.errorMessage) return;

        this.errorMessage.hidden = !state;
      }
    }
  );
}
